import type { YieldEstimationAPI, YieldEstimationForm } from './index';

/**
 * Client-side yield preview computation.
 *
 * IMPORTANT: this MUST mirror the backend, which is the source of truth for the persisted value
 * (`CornYieldEstimationSample.result` / `CornYieldEstimation.result` in
 * `backend/src/apps/crop_monitoring/models.py`). It exists only to preview the result between
 * "Estimar" and "Guardar" without persisting; on save the backend recomputes and overrides it.
 * If the backend formula changes, update this and its guard test (`calc.test.ts`).
 */

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/** Estimated yield (kg/ha) for one sample: spikes/ha * (thousand_grains_weight_kg * 0.001) *
 * grains/spike. The form holds the 1000-grain weight in grams, so it is converted to kg. */
export const sampleResultKgHa = (sample: YieldEstimationForm['samples'][number]): number => {
    const spikes = Number(sample.spikes) || 0;
    const grains = Number(sample.grainsSpike) || 0;
    const weightKg = (Number(sample.weight1000Grains) || 0) / 1000;
    return round3(spikes * (weightKg * 0.001) * grains);
};

/** Build the API payload (with computed per-sample and average results) from the form samples,
 * without persisting. The average matches the backend `CornYieldEstimation.result`. */
export const buildEstimation = (
    cropMonitoringId: number,
    samples: YieldEstimationForm['samples']
): YieldEstimationAPI => {
    const yieldSamples = (samples ?? []).map((sample) => ({
        name: sample.name,
        spikesPerHectare: Number(sample.spikes) || 0,
        grainsPerSpike: Number(sample.grainsSpike) || 0,
        thousandGrainsWeight: (Number(sample.weight1000Grains) || 0) / 1000,
        result: sampleResultKgHa(sample),
    }));
    const result = yieldSamples.length
        ? round3(yieldSamples.reduce((sum, s) => sum + (s.result ?? 0), 0) / yieldSamples.length)
        : 0;
    return { cropMonitoring: cropMonitoringId, status: 'saved', yieldSamples, result };
};
